<script lang="ts" setup>
	import { computed, ref, watch } from 'vue';
	import { useModalStore } from '@/store/modal.store';
	import { useTeamStore } from '@/store/team.store';
	import { useRoute } from 'vue-router';
	import { useAuthStore } from '@/store/auth.store';
	import { usStateMap } from '@/utils/statesList';
	import { ISubscriber } from '@/types';
	import { useSubscribersStore } from '@/store/subscribers.store';
	import DefaultBtn from "@/components/block/DefaultBtn.vue";

	const props = defineProps({
		closeSignal: Boolean
	});

	const selectedOptionAddress = ref();
	const selectedOptionDelivery = ref();
	const isOpenAddress = ref(false);
	const isOpenDelivery = ref(false);
	const teamStore = useTeamStore();
	const modalStore = useModalStore();
	const authStore = useAuthStore();
	const route = useRoute();
	const subscribersStore = useSubscribersStore();
	const loading = ref<boolean>(false);

	const options = Array.from(usStateMap, ([key, value]) => ({
		value: key,
		label: value
	}));

	const subId = computed<string>(() => String(modalStore.modalPayload.id || '-'));
	const subData = ref<ISubscriber | null>(
		subscribersStore.subscribersMap.has(subId.value) && 
		JSON.parse(JSON.stringify(subscribersStore.subscribersMap.get(subId.value))) || null
	);

	watch(subData, (newVal) => {
		if (newVal?.address?.state) {
			const existingState = options.find(option => option.value === newVal.address.state);
			if (existingState) {
				selectedOptionAddress.value = existingState;
			}
		}
		if (newVal?.delivery_address?.state) {
			const existingDelivery = options.find(option => option.value === newVal.delivery_address.state);
			if (existingDelivery) {
				selectedOptionDelivery.value = existingDelivery;
			}
		}
	}, { immediate: true });

	function handleSubmit() {
		if (subData.value) {
			loading.value = true;
			subscribersStore.editSubscriber(subData.value)
				.then(() => {
					modalStore.openModal('SubscriberInfoUpdated');
				}).finally(() => {
					loading.value = false;
				});
		} else {
			loading.value = false;
		}
	}

	const toggleSelectAddress = () => {
		isOpenAddress.value = !isOpenAddress.value;
	};

	const toggleSelectDelivery = () => {
		isOpenDelivery.value = !isOpenDelivery.value;
	};

	const selectOptionAddress = (option: any) => {
		selectedOptionAddress.value = option;
		isOpenAddress.value = false;
		toggleSelectAddress(); 

		if (subData.value && subData.value.address) {
			subData.value.address.state = option.value;
		}
	};

	const selectOptionDelivery = (option: any) => {
		selectedOptionDelivery.value = option;
		isOpenDelivery.value = false;
		toggleSelectDelivery();

		if (subData.value && subData.value.delivery_address) {
			subData.value.delivery_address.state = option.value;
		}
	};

	function areYouSure() {
		modalStore.openModal('SubscriberInfoNoUpdated');
	}

	const formatDate = (date: number | Date) => {
		const d = new Date(date);
		const month = ('0' + (d.getMonth() + 1)).slice(-2);
		const day = ('0' + d.getDate()).slice(-2);
		const year = d.getFullYear();
		return `${month}/${day}/${year}`;
	};

	const parseDate = (value: string): number => {
		const parts = value.split('/');
		if (parts.length === 3) {
			const [month, day, year] = parts.map(Number);
			return new Date(year, month - 1, day).getTime();
		}
		return subData.value?.created || Date.now();
	};

	const formattedDate = computed({
		get() {
			return subData.value?.created ? formatDate(subData.value.created) : '';
		},
		set(value: string) {
			if (subData.value) {
				subData.value.created = parseDate(value); 
			}
		},
	});

	watch(() => subData.value?.phone, (current, prev) => {
		if (current !== prev && subData.value?.phone) {
			const digitsOnly = subData.value?.phone.replace(/\D/g, '');
			subData.value.phone = ('+1' + digitsOnly.replace(/^1/, '')).slice(0, 12);
		}
	});

</script>

<template>

	<div v-if="modalStore.isModalVisible('SubscriberProfileEdit') && subData" class="modal modal--profile modal--active">
		<div class="modal__body">

			<div class="modal__header">
				<p class="modal__subtitle">
					<span class="material-symbols-outlined">edit</span>
					<span>Subscriber Profile Edit</span>
				</p>
				<button @click="modalStore.closeModal" class="modal__close">
					<span class="material-symbols-outlined">close</span>
				</button>
			</div>

			<form @submit.prevent="handleSubmit">
				<div class="modal__main modal__main--scroll">
					<p class="form__title"><span class="material-symbols-outlined">info</span>Subscriber Info</p>
					<div class="modal__main_wrap">

						<div class="login__item">
							<label for="subId">Account ID</label>
							<input :value="(subData.accountId ?? subData.id) || '-'" readonly id="subId" type="text" class="login__password">
							<span class="material-symbols-outlined login__item_ico">fingerprint</span>
						</div>

						<div class="login__item">
							<label for="dateEnroller">Date Enrolled</label>
							<input
								v-model="formattedDate"
								readonly
								id="dateEnroller"
								type="text"
								class="login__password"
							/>
							<span class="material-symbols-outlined login__item_ico date__ico">
								calendar_month
							</span>
						</div>

						<div class="login__item">
							<label for="firstName">First Name</label>
							<input v-model="subData.name"  id="firstName" type="text" placeholder="First Name" class="login__password">
							<span class="material-symbols-outlined login__item_ico">person</span>
						</div>

						<div class="login__item">
							<label for="lastName">Last Name</label>
							<input v-model="subData.lastName" id="lastName" type="text" placeholder="Last Name" class="login__password">
							<span class="material-symbols-outlined login__item_ico">person</span>
						</div>

						<div class="login__item">
							<label for="email">Email</label>
							<input v-model="subData.email" id="email" type="email"  placeholder="Email" class="login__password">
							<span class="material-symbols-outlined login__item_ico">mail</span>
						</div>

						<div class="login__item">
							<label for="phoneNumber">Phone Number</label>
							<input v-model="subData.phone" id="phoneNumber" type="text"  placeholder="Phone Number" class="login__password">
							<span class="material-symbols-outlined login__item_ico">phone</span>
						</div>

					</div>

					<p class="form__title"><span class="material-symbols-outlined">home</span>Address</p>
					<div class="modal__main_wrap">
						<div class="login__item">
							<label for="addresLineOne">Address Line 1</label>
							<input v-model="subData.address.address1" id="addresLineOne" type="text"  placeholder="Address Line 1" class="login__password">
							<span class="material-symbols-outlined login__item_ico">location_on</span>
						</div>

						<div class="login__item">
							<label for="addresLineTwo">Address Line 2</label>
							<input v-model="subData.address.address2" id="addresLineTwo" type="text" placeholder="Address Line 2"  class="login__password">
							<span class="material-symbols-outlined login__item_ico">location_on</span>
						</div>

						<div class="login__item">
							<label for="city">City</label>
							<input v-model="subData.address.city" id="city" type="text" placeholder="City"  class="login__password">
							<span class="material-symbols-outlined login__item_ico">location_on</span>
						</div>

						<div class="login__item login__item-wrap">
							<div class="login__item login__block">
								<div class="pagination__select">
									<p>State</p>
									<div class="custom-select login__email" @click="toggleSelectAddress">
										<span class="material-symbols-outlined login__item_ico state__ico">location_on</span>
										<span class="state__sm">{{ selectedOptionAddress ? selectedOptionAddress.value : 'State' }}</span>
										<span class="material-symbols-outlined expand_more">expand_more</span>
										<ul :class="{ 'show': isOpenAddress }">
											<li v-for="option in options" :key="option.value" @click="selectOptionAddress(option)"
												:class="{ 'active': option.value === selectedOptionAddress?.value }">
												{{ option.label }}
											</li>
										</ul>
									</div>
								</div>
							</div>

							<div class="login__item login__block">
								<label for="zipCode">Zip Code</label>
								<input v-model="subData.address.zipCode" id="zipCode" type="text" class="login__password login__zip">
								<span class="material-symbols-outlined login__item_ico">location_on</span>
							</div>
						</div>
					</div>

					<p class="form__title"><span class="material-symbols-outlined">home</span>Delivery Address</p>
					<div class="modal__main_wrap">
						<div class="login__item">
							<label for="addresLineTree">Address Line 1</label>
							<input v-model="subData.delivery_address.address1" id="addresLineTree" placeholder="Address Line 1"  type="text"
								class="login__password">
							<span class="material-symbols-outlined login__item_ico">location_on</span>
						</div>

						<div class="login__item">
							<label for="addresLineFour">Address Line 2</label>
							<input v-model="subData.delivery_address.address2" id="addresLineFour" placeholder="Address Line 2"  type="text"
								class="login__password">
							<span class="material-symbols-outlined login__item_ico">location_on</span>
						</div>

						<div class="login__item">
							<label for="cityTwo">City</label>
							<input v-model="subData.delivery_address.city" id="cityTwo" type="text" placeholder="City"  class="login__password">
							<span class="material-symbols-outlined login__item_ico">location_on</span>
						</div>

						<div class="login__item login__item-wrap">
							<div class="login__item login__block">
								<div class="pagination__select">
									<p>State</p>
									<div class="custom-select login__email" @click="toggleSelectDelivery">
										<span class="material-symbols-outlined login__item_ico state__ico">location_on</span>
										<span class="state__sm">{{ selectedOptionDelivery ? selectedOptionDelivery.value : 'State' }}</span>
										<span class="material-symbols-outlined expand_more">expand_more</span>
										<ul :class="{ 'show': isOpenDelivery }">
											<li v-for="option in options" :key="option.value" @click="selectOptionDelivery(option)"
												:class="{ 'active': option.value === selectedOptionDelivery?.value }">
												{{ option.label }}
											</li>
										</ul>
									</div>
								</div>
							</div>

							<div class="login__item login__block">
								<label for="zipCodeTwo">Zip Code</label>
								<input v-model="subData.delivery_address.zipCode" id="zipCodeTwo" placeholder="Zip Code"  type="text"
									class="login__password login__zip">
								<span class="material-symbols-outlined login__item_ico">location_on</span>
							</div>
						</div>
					</div>

					<div class="modal__btn">
						<button class="btn btn--white" type="button" @click="areYouSure">Undo</button>
						<DefaultBtn  type="submit" class="btn btn__save" :loading="loading" text="Save"/>
					</div>
				</div>
			</form>

		</div>
	</div>

	<div v-if="modalStore.isModalVisible('SubscriberInfoUpdated')" class="modal modal--profile modal--active">
		<div class="modal__body">
			<div class="modal__header">
				<p class="modal__subtitle">
					<span class="material-symbols-outlined">edit</span>
					<span>Subscriber Profile Edit</span>
				</p>
				<button @click="modalStore.closeModal" class="modal__close">
					<span class="material-symbols-outlined">close</span>
				</button>
			</div>
			<div class="modal__main">
				<svg class="modal__update_svg" width="93" height="94" viewBox="0 0 93 94" fill="none"
					xmlns="http://www.w3.org/2000/svg">
					<path
						d="M39.5532 57.505L28.1823 46.1341C27.5046 45.4564 26.6527 45.1096 25.6267 45.094C24.6007 45.0782 23.7331 45.425 23.024 46.1341C22.315 46.8432 21.9604 47.7029 21.9604 48.7132C21.9604 49.7234 22.315 50.5831 23.024 51.2922L36.4563 64.7245C37.3411 65.6093 38.3734 66.0517 39.5532 66.0517C40.733 66.0517 41.7652 65.6093 42.65 64.7245L69.8817 37.4928C70.5595 36.8151 70.9062 35.9633 70.9219 34.9373C70.9376 33.9112 70.5909 33.0437 69.8817 32.3346C69.1726 31.6255 68.313 31.271 67.3027 31.271C66.2924 31.271 65.4327 31.6255 64.7236 32.3346L39.5532 57.505ZM46.5082 93.5C40.0768 93.5 34.0316 92.2796 28.3727 89.8387C22.7136 87.3979 17.791 84.0854 13.605 79.9012C9.4188 75.7169 6.10477 70.7965 3.66286 65.1399C1.22096 59.4835 0 53.4396 0 47.0082C0 40.5768 1.22042 34.5316 3.66127 28.8727C6.10212 23.2136 9.41464 18.291 13.5988 14.105C17.7831 9.91881 22.7035 6.60478 28.3601 4.16287C34.0165 1.72096 40.0604 0.5 46.4918 0.5C52.9232 0.5 58.9684 1.72043 64.6273 4.16128C70.2864 6.60213 75.209 9.91464 79.395 14.0988C83.5812 18.2831 86.8952 23.2035 89.3371 28.8601C91.779 34.5165 93 40.5604 93 46.9918C93 53.4232 91.7796 59.4683 89.3387 65.1273C86.8979 70.7864 83.5854 75.709 79.4012 79.895C75.2169 84.0812 70.2965 87.3952 64.6399 89.8371C58.9835 92.279 52.9396 93.5 46.5082 93.5ZM46.5 86.158C57.4316 86.158 66.6908 82.3646 74.2777 74.7777C81.8646 67.1908 85.658 57.9316 85.658 47C85.658 36.0684 81.8646 26.8092 74.2777 19.2223C66.6908 11.6354 57.4316 7.842 46.5 7.842C35.5684 7.842 26.3092 11.6354 18.7223 19.2223C11.1354 26.8092 7.342 36.0684 7.342 47C7.342 57.9316 11.1354 67.1908 18.7223 74.7777C26.3092 82.3646 35.5684 86.158 46.5 86.158Z"
						fill="#2563EB" />
				</svg>
				<p class="modal__update">Profile information updated!</p>
			</div>
		</div>
	</div>

	<div v-if="modalStore.isModalVisible('SubscriberInfoNoUpdated')" class="modal modal--profile modal--active">
		<div class="modal__body">
			<div class="modal__header">
				<p class="modal__subtitle">
					<span class="material-symbols-outlined">edit</span>
					<span>Device Info Edit</span>
				</p>
				<button @click="modalStore.closeModal" class="modal__close">
					<span class="material-symbols-outlined">close</span>
				</button>
			</div>
			<div class="modal__main">
				<svg class="modal__update_svg" width="100" height="100" viewBox="0 0 100 100" fill="none"
					xmlns="http://www.w3.org/2000/svg">
					<path
						d="M49.9429 79.0138C51.4181 79.0138 52.6642 78.5045 53.681 77.4859C54.6977 76.4673 55.206 75.2204 55.206 73.7451C55.206 72.2699 54.6967 71.0239 53.6781 70.0072C52.6595 68.9904 51.4126 68.482 49.9373 68.482C48.4621 68.482 47.2161 68.9913 46.1994 70.0099C45.1826 71.0285 44.6742 72.2754 44.6742 73.7507C44.6742 75.2259 45.1835 76.472 46.2021 77.4888C47.2207 78.5055 48.4676 79.0138 49.9429 79.0138ZM50.0084 99.0844C43.2197 99.0844 36.8387 97.7962 30.8653 95.2197C24.8919 92.6433 19.6959 89.1467 15.2773 84.7301C10.8585 80.3134 7.36041 75.1196 4.78285 69.1488C2.20529 63.1782 0.916504 56.7985 0.916504 50.0098C0.916504 43.2212 2.20473 36.8402 4.78117 30.8668C7.35762 24.8934 10.8542 19.6973 15.2708 15.2787C19.6875 10.86 24.8813 7.36188 30.852 4.78432C36.8227 2.20676 43.2024 0.917969 49.9911 0.917969C56.7797 0.917969 63.1607 2.2062 69.1341 4.78264C75.1075 7.35909 80.3036 10.8556 84.7222 15.2723C89.1409 19.689 92.639 24.8827 95.2166 30.8535C97.7941 36.8242 99.0829 43.2039 99.0829 49.9925C99.0829 56.7812 97.7947 63.1622 95.2183 69.1355C92.6418 75.109 89.1453 80.305 84.7286 84.7236C80.3119 89.1423 75.1182 92.6405 69.1474 95.218C63.1767 97.7956 56.797 99.0844 50.0084 99.0844ZM49.9997 91.3345C61.5386 91.3345 71.3122 87.3304 79.3205 79.322C87.3289 71.3137 91.3331 61.5401 91.3331 50.0012C91.3331 38.4623 87.3289 28.6887 79.3205 20.6803C71.3122 12.672 61.5386 8.66784 49.9997 8.66784C38.4608 8.66784 28.6872 12.672 20.6789 20.6803C12.6705 28.6887 8.66638 38.4623 8.66638 50.0012C8.66638 61.5401 12.6705 71.3137 20.6789 79.322C28.6872 87.3304 38.4608 91.3345 49.9997 91.3345ZM50.4182 27.8838C52.802 27.8838 54.865 28.6343 56.6071 30.1351C58.3492 31.6359 59.2203 33.5119 59.2203 35.7631C59.2203 37.6576 58.6639 39.3533 57.5511 40.8502C56.4382 42.3473 55.1665 43.7416 53.7357 45.0333C51.8602 46.6971 50.209 48.5272 48.7819 50.5238C47.355 52.5203 46.5918 54.744 46.4924 57.1949C46.4593 58.1354 46.7905 58.9236 47.486 59.5595C48.1815 60.1954 48.9929 60.5133 49.9203 60.5133C50.9138 60.5133 51.755 60.1821 52.4439 59.5197C53.1327 58.8574 53.5732 58.0459 53.7653 57.0855C54.1098 55.3103 54.8434 53.7288 55.9662 52.3412C57.0889 50.9534 58.3052 49.6338 59.615 48.3825C61.4979 46.5471 63.1415 44.5459 64.5457 42.3788C65.95 40.2116 66.6521 37.7945 66.6521 35.1272C66.6521 31.0337 65.0392 27.6704 61.8134 25.0374C58.5876 22.4044 54.8219 21.0879 50.5164 21.0879C47.4429 21.0879 44.545 21.7685 41.8226 23.1297C39.1002 24.4909 36.9805 26.4764 35.4636 29.0863C34.9933 29.8877 34.8423 30.7313 35.0107 31.617C35.179 32.5027 35.6336 33.1752 36.3743 33.6345C37.3171 34.1593 38.2853 34.3091 39.2789 34.0838C40.2724 33.8587 41.117 33.2924 41.8125 32.3849C42.8591 31.0269 44.1259 29.9373 45.613 29.116C47.1 28.2945 48.7018 27.8838 50.4182 27.8838Z"
						fill="#FF3F6D" />
				</svg>

				<p class="modal__update">Are you sure?</p>
				<p class="modal__update_text">You are about to close this page without saving.</p>
				<div class="modal__update_btn">
					<button class="btn btn--white btn--border" type="button" @click="modalStore.closeModal">Close</button>
					<button class="btn" type="submit" @click="modalStore.openModal('SubscriberProfileEdit')">Continue</button>
				</div>
			</div>
		</div>
	</div>

</template>

<style scoped>
	.modal__main{
		overflow: auto;
		max-height: 500px;
		justify-content: center;
		padding-top: 25px;
	}
	.modal__main--scroll{
		justify-content: start;
	}
	.btn--white{
		border: 1px solid #E2E8F0
	}

	.form__title{
		display: flex;
		align-items: center;
		gap: 10px;
		margin: 0  25px 20px 25px;
		color: #2563EB;
		font-weight: 600;
	}
	.form__title path{
		fill: #2563EB;
	}
	.modal__btn{
		margin-top: 20px;
	}
	.login__item-wrap{
		display: flex;
		gap: 20px;
	}

	.login__zip{
		width: 100%;
	}

	.login__block{
		width: 50%;
		margin-bottom: 0;
	}

	.custom-select {
		position: relative;
		cursor: pointer;
		display: flex;
		align-items: center;
		justify-content: space-between;
		height: 55px;
		width: 100%;
		padding: 15px 20px 15px 50px;
		border-radius: 8px;
		border: 1px solid #93C5FD;
		text-transform: uppercase;
		outline: none;
		color: #0f172a;
	}
	.pagination__select p {
		display: block;
		color: #64748b;
		margin-bottom: 3px;
		font-size: 16px;
	}
	.custom-select ul {
		position: absolute;
		left: 0;
		bottom: 100%;
		width: 100%;
		z-index: 1;
		display: none;
		background-color: #fff;
		border: 1px solid #ccc;
		box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
		padding: 0;
		margin: 0;
		border-radius: 8px;
	}

	.login__item_ico--select{
		top: 48%;
	}
	.custom-select li {
		padding: 10px;
		cursor: pointer;
		text-transform: uppercase;
	}

	.custom-select ul.show {
		display: block;
	}

	.custom-select ul li {
		list-style: none;
		padding: 8px 12px;
		transition: all .3s;
	}

	.custom-select ul li:hover {
		background-color: #F8FAFC;
	}

	.custom-select ul li.active {
		background-color: #f0f0f0;
	}

	.show{
		overflow: auto;
		max-height: 200px;
	}

	.show li {
		text-transform:  capitalize;
	}

	.login__item_ico{
		top: 63%;
	}

	.date-picker{
		position: absolute;
		z-index: 11;
		background: #fff;	
	}
	.date__ico{
		cursor: pointer;
	}
	.state__ico{
		top: 50%;
	}
	.state__sm{
		text-transform:  capitalize;
	}
	.btn__save{
		box-shadow: none;
	}


</style>